import { useState, useRef } from "react";
import { toast } from "react-toastify";
import { QRCodeCanvas } from "qrcode.react";
import {
  MdContentCopy,
  MdStore,
  MdQrCode,
  MdDownload,
} from "react-icons/md";
import { useTranslation } from "react-i18next";
import { colorThemes } from "../../../helpers/csshelper";
import { QrSizeOptions } from "../../../components/common/utlity/options";
import html2canvas from "html2canvas";
import { AWS_URL } from "../../../services/http.service";

export function QRCodeModal({ open, onClose, storeUrl, businessName, businessLogo }) {
  const { t } = useTranslation();
  const [qrSize, setQrSize] = useState(256);
  const [includeLogo, setIncludeLogo] = useState(false);
  const [qrBgColor, setQrBgColor] = useState("#ffffff");
  const [qrFgColor, setQrFgColor] = useState("#000000");
  const qrRef = useRef(null);

const downloadQRCode = () => {
  if (!qrRef.current) return;

  const canvas = qrRef.current.querySelector("canvas");
  if (!canvas) return;

  // Create a new canvas with extra space for text
  const textCanvas = document.createElement('canvas');
  const ctx = textCanvas.getContext('2d');
  
  // Set dimensions (add 60px for text area)
  textCanvas.width = canvas.width;
  textCanvas.height = canvas.height + 60;
  
  // Fill background
  ctx.fillStyle = qrBgColor;
  ctx.fillRect(0, 0, textCanvas.width, textCanvas.height);
  
  // Draw the QR code on the new canvas
  ctx.drawImage(canvas, 0, 0);
  
  // Add text below QR code
  ctx.font = 'bold 16px Arial';
  ctx.fillStyle = qrFgColor;
  ctx.textAlign = 'center';
  ctx.fillText(`Scan to visit ${businessName} store`, textCanvas.width / 2, canvas.height + 30);

  // Convert to PNG and download
  const pngUrl = textCanvas.toDataURL("image/png");
  const downloadLink = document.createElement("a");
  downloadLink.href = pngUrl;
  downloadLink.download = `${businessName.replace(/\s+/g, "-")}-store-qr.png`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  toast.success(t("QRCODE.DOWNLOAD_SUCCESS"));
};

  const shareViaWhatsApp = () => {
    const text = `${t("QRCODE.SCAN_TO_VISIT")} ${businessName} ${t("QRCODE.STORE")}: ${storeUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  const copyStoreUrl = async () => {
    try {
      await navigator.clipboard.writeText(storeUrl);
      toast.success(t("QRCODE.URL_COPIED"));
    } catch (error) {
      toast.error(t("QRCODE.COPY_FAILED"));
    }
  };

  if (!open) return null;

  const logoUrl = businessLogo && businessLogo.startsWith("http") 
    ? businessLogo 
    : businessLogo 
      ? AWS_URL + businessLogo 
      : null;

  return (
    <div className='fixed z-50 inset-0 bg-black bg-opacity-40 dark:bg-black dark:bg-opacity-70 flex items-center justify-center backdrop-blur-sm animate-fadeIn'>
      <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-scaleIn flex flex-col max-h-[90vh]'>
        <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-gray-800 dark:to-gray-900'>
          <div className='flex items-center justify-between'>
            <h2 className='text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2'>
              <MdQrCode className="text-blue-600 dark:text-blue-400" />
              {t("QRCODE.STORE_QR_CODE")}
            </h2>
            <button
              onClick={onClose}
              className='text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white text-2xl font-bold transition-colors'
            >
              &times;
            </button>
          </div>
        </div>

        <div className='px-6 py-4 bg-white dark:bg-gray-800 overflow-y-auto flex-1'>
          <div className="flex flex-col items-center justify-center mb-6">
            <div
              ref={qrRef}
              className="p-6 bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg mb-4 relative"
              style={{ backgroundColor: qrBgColor }}
            >
              <QRCodeCanvas
                value={storeUrl}
                size={qrSize}
                bgColor={qrBgColor}
                fgColor={qrFgColor}
                level="H"
                includeMargin={true}
              />
              {includeLogo && (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="w-16 h-16 bg-white dark:bg-gray-800 rounded-full p-2 border-4 border-white dark:border-gray-800 shadow-lg">
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt="Store Logo" 
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                        <MdStore className="text-white text-2xl" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-2">
              {t("QRCODE.SCAN_TO_VISIT")} {businessName} {t("QRCODE.STORE")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center break-all px-4 mb-6">
              {storeUrl}
            </p>
          </div>

          <div className="space-y-6 mb-6">
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                {t("QRCODE.QR_CODE_SIZE")}
              </label>
              <div className="flex items-center justify-between">
                {QrSizeOptions.map((option) => (
                  <button
                    key={option.size}
                    onClick={() => setQrSize(option.size)}
                    className={`flex flex-col items-center px-4 py-3 rounded-lg transition-all ${
                      qrSize === option.size
                        ? 'bg-blue-600 text-white shadow-md dark:bg-blue-500'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <span className="font-medium">{t(option.translationKey || option.label)}</span>
                    <span className="text-xs mt-1">{option.size}px</span>
                  </button>
                ))}
              </div>
            </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
    {t("QRCODE.QR_CODE_COLORS")}
  </label>

  <div className="grid grid-cols-2 gap-4 mb-4">
    {/* Background Color */}
    <div className="w-full min-w-0"> {/* Added min-w-0 */}
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
        {t("QRCODE.BACKGROUND_COLOR")}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={qrBgColor}
          onChange={(e) => setQrBgColor(e.target.value)}
          className="w-10 h-10 cursor-pointer rounded-lg border border-gray-300 dark:border-gray-600 flex-shrink-0"
        />
        <input
          type="text"
          value={qrBgColor}
          onChange={(e) => setQrBgColor(e.target.value)}
          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>
    </div>

    {/* QR Color (Foreground) */}
    <div className="w-full min-w-0"> {/* Added min-w-0 */}
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
        {t("QRCODE.QR_COLOR")}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={qrFgColor}
          onChange={(e) => setQrFgColor(e.target.value)}
          className="w-10 h-10 cursor-pointer rounded-lg border border-gray-300 dark:border-gray-600 flex-shrink-0"
        />
        <input
          type="text"
          value={qrFgColor}
          onChange={(e) => setQrFgColor(e.target.value)}
          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>
    </div>
  </div>

  {/* Quick Themes */}
  <div className="w-full overflow-hidden"> {/* Added overflow-hidden */}
    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
      {t("QRCODE.QUICK_THEMES")}
    </label>
    <div className="grid grid-cols-3 gap-2">
      {colorThemes.map((theme) => (
        <button
          key={theme.name}
          onClick={() => {
            setQrBgColor(theme.bg);
            setQrFgColor(theme.fg);
          }}
          className="flex flex-col items-center p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 hover:shadow-sm transition-all"
          title={theme.name}
        >
          <div className="flex w-full mb-1">
            <div
              className="flex-1 h-3 rounded-l"
              style={{ backgroundColor: theme.bg }}
            />
            <div
              className="flex-1 h-3 rounded-r"
              style={{ backgroundColor: theme.fg }}
            />
          </div>
          <span className="text-xs text-gray-600 dark:text-gray-300 truncate w-full text-center">{theme.name}</span>
        </button>
      ))}
    </div>
  </div>
</div>

         

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-4 rounded-xl border border-blue-100 dark:border-blue-800">
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
                <MdStore size={16} />
                {t("QRCODE.STORE_INFORMATION")}
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300">{t("QRCODE.STORE_NAME")}:</span>
                  <span className="font-medium text-gray-800 dark:text-white">{businessName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300">{t("QRCODE.STORE_URL")}:</span>
                  <span className="font-medium text-blue-600 dark:text-blue-400 text-xs truncate ml-2">{storeUrl}</span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {t("QRCODE.ENCODED_INFO")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky bottom-0">
          <div className="space-y-3">
            <button
              onClick={downloadQRCode}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 dark:hover:from-blue-600 dark:hover:to-indigo-600 transition-all shadow-md hover:shadow-lg"
            >
              <MdDownload size={20} />
              {t("QRCODE.DOWNLOAD_QR_CODE")}
            </button>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={shareViaWhatsApp}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 dark:from-green-600 dark:to-emerald-700 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 dark:hover:from-green-700 dark:hover:to-emerald-800 transition-all"
              >
                <span className="text-lg">📱</span>
                {t("QRCODE.SHARE_WHATSAPP")}
              </button>
              <button
                onClick={copyStoreUrl}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-gray-600 to-gray-700 dark:from-gray-700 dark:to-gray-800 text-white rounded-lg font-semibold hover:from-gray-700 hover:to-gray-800 dark:hover:from-gray-800 dark:hover:to-gray-900 transition-all"
              >
                <MdContentCopy size={18} />
                {t("QRCODE.COPY_URL")}
              </button>
            </div>

            <button
              onClick={onClose}
              className="w-full px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              {t("RESELLER_ORDERS.CLOSE")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}